import clsx from 'clsx';
import { ArrowRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React, { type JSX } from 'react';
import { BulletSeparatedList, BulletSeparatedListItem } from '../BulletSeparatedList';
import { PrefetchLink } from '../PrefetchLink';
import styles from './ArticleCard.module.scss';

export type ArticleCardSize = 'small' | 'medium' | 'large';

interface ArticleCardProps {
  id: string;
  size?: ArticleCardSize;
  image?: {
    src: string;
    srcSet?: string;
    webpSrcSet?: string;
    alt: string;
    width?: number;
    height?: number;
    sizes?: string;
    fetchPriority?: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
    loading?: React.ImgHTMLAttributes<HTMLImageElement>['loading'];
  };
  href: string;
  title: string;
  excerpt?: string;
  date?: string;
  readingTime: string;
  preTitle?: string;
  testId: string;
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  meta?: React.ReactNode;
  onClick?: () => void;
}

export const ArticleCard: React.FC<ArticleCardProps> = ({
  id,
  size = 'medium',
  image,
  href,
  title,
  excerpt,
  date,
  readingTime,
  preTitle,
  testId,
  headingLevel = 2,
  meta,
  onClick,
}) => {
  const ImageWrapper = image?.webpSrcSet ? 'picture' : React.Fragment;
  const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;
  const t = useTranslations('pages.articles.articleList.articleCard');
  const tComponent = useTranslations('components.articleCard');

  return (
    <article
      className={clsx(styles.article, {
        [styles.articleSmall]: size === 'small',
        [styles.articleMedium]: size === 'medium',
        [styles.articleLarge]: size === 'large',
      })}
      aria-labelledby={`${id}-title`}
      data-testid={testId}
    >
      {image && (
        <ImageWrapper>
          {image.webpSrcSet && (
            <source type="image/webp" srcSet={image.webpSrcSet} sizes={image.sizes} />
          )}
          <img
            src={image.src}
            srcSet={image.srcSet}
            sizes={image.sizes}
            alt={image.alt}
            width={image.width}
            height={image.height}
            className={styles.image}
            fetchPriority={image.fetchPriority}
            loading={image.loading}
          />
        </ImageWrapper>
      )}
      <div className={styles.body}>
        {preTitle && <p className={styles.preTitle}>{preTitle}</p>}
        <HeadingTag className={styles.title} id={`${id}-title`} data-testid={`${testId}-title`}>
          <PrefetchLink
            href={href}
            className={styles.link}
            data-testid={`${testId}-link`}
            prefetchOnHover={true}
            prefetchWhenVisible={true}
            onClick={onClick}
          >
            {title}
          </PrefetchLink>
        </HeadingTag>
        {excerpt && size !== 'small' && <p className={styles.excerpt}>{excerpt}</p>}
        <footer className={styles.footer}>
          <div
            role="group"
            aria-label={tComponent('articleMetaInformation')}
            className={styles.meta}
          >
            <BulletSeparatedList
              items={
                <>
                  {date && <BulletSeparatedListItem>{date}</BulletSeparatedListItem>}
                  <BulletSeparatedListItem>
                    {t('readingTime', { readingTime })}
                  </BulletSeparatedListItem>
                </>
              }
            />
          </div>
          <span className={styles.icon} aria-hidden="true">
            <ArrowRight />
          </span>
        </footer>
      </div>
      {meta && <span className={styles.badges}>{meta}</span>}
    </article>
  );
};
